import React from "react";
import {
  Container, Typography, Grid, Card, CardContent, Box, Chip
} from "@mui/material";
import ComputerIcon from "@mui/icons-material/Computer";
import StorefrontIcon from "@mui/icons-material/Storefront";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import FacebookIcon from "@mui/icons-material/Facebook";
import RoomIcon from "@mui/icons-material/Room";
import BrushIcon from "@mui/icons-material/Brush";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Helmet } from "react-helmet";

const services = [
  {
    icon: ComputerIcon,
    titleKey: "web_development",
    descKey: "web_development_desc",
    color: "#3b82f6",
    badge: "core"
  },
  {
    icon: StorefrontIcon,
    titleKey: "eshop",
    descKey: "eshop_desc",
    color: "#f59e42",
    badge: "core"
  },
  {
    icon: MobileFriendlyIcon,
    titleKey: "mobile_apps",
    descKey: "mobile_apps_desc",
    color: "#22c55e"
  },
  {
    icon: IntegrationInstructionsIcon,
    titleKey: "custom_software",
    descKey: "custom_software_desc",
    color: "#f43f5e"
  },
  {
    icon: TrendingUpIcon,
    titleKey: "seo",
    descKey: "seo_desc",
    color: "#8c53ff",
    badge: "premium"
  },
  {
    icon: FacebookIcon,
    titleKey: "facebook_pages",
    descKey: "facebook_pages_desc",
    color: "#4267B2"
  },
  {
    icon: RoomIcon,
    titleKey: "google_maps",
    descKey: "google_maps_desc",
    color: "#32a852"
  },
  {
    icon: BrushIcon,
    titleKey: "branding",
    descKey: "branding_desc",
    color: "#f76c8c"
  }
];

const badgeLabels = {
  core: { label: "Βασική", color: "primary" },
  premium: { label: "Δημοφιλής", color: "secondary" }
};

const Services = () => {
  const { t } = useTranslation();

  return (
    <>
      <Helmet>
        <title>{t("meta_title_services")}</title>
        <meta name="description" content={t("meta_description")} />
        <link rel="alternate" hrefLang="en" href="brainstorm-visions.com/en" />
        <link rel="alternate" hrefLang="el" href="brainstorm-visions.com/el" />
      </Helmet>
      <Box
        sx={{
          background: "linear-gradient(135deg, #f0f4f8 0%, #e0ecf7 100%)",
          py: { xs: 5, md: 9 },
          minHeight: "100vh"
        }}
      >
        <Container>
          <Typography
            variant="h3"
            fontWeight="bold"
            align="center"
            sx={{
              mb: 2,
              color: "primary.main",
              letterSpacing: 1.2,
              textShadow: "0 2px 8px #9db9ce44",
              fontSize: { xs: "2rem", md: "2.8rem" }
            }}
            component={motion.div}
            initial={{ opacity: 0, y: -40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            {t("services_page")}
          </Typography>
          <Typography
            variant="h6"
            align="center"
            color="text.secondary"
            mb={6}
            component={motion.div}
            initial={{ opacity: 0, y: -15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            sx={{ fontSize: { xs: "1.1rem", md: "1.22rem" } }}
          >
            {t("services_sub")}
          </Typography>
          <Grid container spacing={5} justifyContent="center">
            {services.map((service, i) => {
              const Icon = service.icon;
              return (
                <Grid item xs={12} sm={6} md={3} key={i}>
                  <motion.div
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.7, delay: i * 0.08 }}
                    whileHover={{ scale: 1.06, boxShadow: "0 8px 40px #4a90e229" }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        p: 3,
                        borderRadius: 4,
                        textAlign: "center",
                        boxShadow: 6,
                        background: "#fff",
                        position: "relative",
                        overflow: "visible"
                      }}
                    >
                      <Box
                        sx={{
                          width: 78,
                          height: 78,
                          background: `linear-gradient(135deg, ${service.color} 70%, #fff 100%)`,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mt: -7,
                          mb: 2,
                          boxShadow: 3,
                          border: "4px solid #fff"
                        }}
                      >
                        <Icon sx={{ fontSize: 42, color: "#fff", textShadow: "0 2px 8px #0003" }} />
                      </Box>
                      <CardContent>
                        <Typography variant="h6" fontWeight="bold" mb={1} color="primary">
                          {t(service.titleKey)}
                        </Typography>
                        {service.badge && (
                          <Chip
                            label={badgeLabels[service.badge].label}
                            color={badgeLabels[service.badge].color}
                            size="small"
                            sx={{ mb: 1, fontWeight: "bold" }}
                          />
                        )}
                        <Typography variant="body2" color="text.secondary" fontSize={16} minHeight={62}>
                          {t(service.descKey)}
                        </Typography>
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default Services;
